import { apiRequest } from "./client";
import { getApiBaseUrl } from "../lib/env";
import { AuthTokens, AuthUser } from "../types/auth";

export type LoginRequest = {
  username: string;
  password: string;
};

export type LoginResponse = {
  tokens: AuthTokens;
  user: AuthUser;
};

/**
 * Mock login API.
 *
 * Blueprint rule: UI should call src/api/* instead of fetch directly.
 * In mock mode, this resolves via mockFetch; when a backend exists, set
 * VITE_API_BASE_URL and this will POST to /auth/login.
 */
export async function login(body: LoginRequest): Promise<LoginResponse> {
  const data = await apiRequest<
    {
      accessToken?: string;
      refreshToken?: string;
      access?: string;
      refresh?: string;
      access_token?: string;
      refresh_token?: string;
      user?: {
        id?: string;
        username?: string;
        name?: string;
        email?: string;
        role?: AuthUser["role"];
      };
    },
    { email: string; password: string }
  >({
    path: "/auth/login",
    method: "POST",
    body: { email: body.username, password: body.password },
    auth: false,
  });

  const access = data.accessToken || data.access || data.access_token;
  const refresh = data.refreshToken || data.refresh || data.refresh_token;
  if (!access || !refresh) {
    throw new Error("Invalid login response: missing tokens");
  }

  const user = data.user;
  if (!user || !user.id || !(user.username || user.name) || !user.email || !user.role) {
    throw new Error("Invalid login response: missing user profile");
  }

  return {
    tokens: {
      access,
      refresh,
    },
    user: {
      id: user.id,
      username: user.username || user.name || user.id,
      email: user.email,
      role: user.role,
    },
  };
}

/**
 * Logout API.
 * - Expects both access & refresh tokens (single-key storage).
 * - If API base is not configured, resolve via mock (no-op).
 * - If API base exists, POST to /auth/logout with refresh token.
 */
export async function logout(tokens: AuthTokens): Promise<void> {
  const base = getApiBaseUrl();
  if (!base) {
    return;
  }
  await apiRequest<void, { refreshToken: string }>({
    path: "/auth/logout",
    method: "POST",
    body: { refreshToken: tokens.refresh },
    auth: false,
  });
}

export async function getProfile(): Promise<AuthUser> {
  const data = await apiRequest<{
    id: string;
    username: string;
    email: string;
    role: AuthUser["role"];
  }>({
    path: "/auth/me",
    method: "GET",
    allowRefresh: false,
  });

  return {
    id: data.id,
    username: data.username,
    email: data.email,
    role: data.role,
  };
}
